/**
 * Split the given duration (in seconds) to hours, minutes, and seconds.
 *
 * @param duration Duration in seconds
 */
export const splitDuration = (duration: number) => {
	let seconds = duration;
	const hours = Math.floor(seconds / 60 / 60);
	seconds -= hours * 60 * 60;

	const minutes = Math.floor(seconds / 60);
	seconds -= minutes * 60;

	return { hours, minutes, seconds };
};

export const timeToSeconds = (timeValue: string): number => {
	const timeParts = timeValue.split(':');
	const hours = parseFloat(timeParts[0].replace(/,/g, '.')) || 0;
	const minutes = parseInt(timeParts[1]) || 0;
	const seconds = parseInt(timeParts[2]) || 0;

	return hours * 3600 + minutes * 60 + seconds;
};

export const timeToHours = (timeValue: string): number => {
	return timeToSeconds(timeValue) / 3600;
};

export const hoursToTime = (hours: number): string => {
	let wholeHours = Math.floor(hours);
	let minutes = Math.round((hours - wholeHours) * 60);

	if (minutes === 60) {
		wholeHours += 1;
		minutes = 0;
	}

	if (minutes === 0) {
		return `${wholeHours}`;
	}

	return `${wholeHours}:${minutes.toString().padStart(2, '0')}`;
};

export const roundDurationToHours = (duration: number) => {
	return Math.round(duration / 3600) * 3600;
};

export const isWeekend = (date: Date) => {
	const dayOfWeek = date.getDay();
	return dayOfWeek === 6 || dayOfWeek === 0;
};

/**
 * Convert seconds to hours, rounding to two decimal places.
 *
 * @param seconds Duration in seconds
 * @returns Duration in hours, rounded to two decimal places
 *
 * @example
 * secondsToHours(7200); // returns 2
 * secondsToHours(6800); // returns 1.89
 */
export const secondsToHours = (seconds: number): number => {
	return Math.round((seconds / 60 / 60) * 100) / 100;
};

/**
 * Convert seconds to hours and minutes.
 *
 * @param seconds Duration in seconds
 * @returns Object containing full hours and remaining minutes
 *
 * @example
 * secondsToHoursAndMinutes(-900); // returns '0h'
 * secondsToHoursAndMinutes(3600); // returns '1h'
 * secondsToHoursAndMinutes(5400); // returns '1h 30m'
 */
export const secondsToHoursAndMinutes = (seconds: number): string => {
	if (seconds <= 0) {
		return `0h`;
	}

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);

	if (minutes === 0) {
		return `${hours}h`;
	}

	return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
};

/**
 * Format seconds to time allocation string (always "0:minutes" format).
 * Used for time allocation inputs where the format is always "0:" followed by total minutes.
 * Note: Minutes can be fractional (e.g., "0:7.5" for 450 seconds).
 *
 * @param seconds Duration in seconds
 * @returns Formatted string in "0:minutes" format (e.g., "0:30", "0:7.5", "0:60")
 *
 * @example
 * secondsToTimeAllocation(1800); // returns "0:30"
 * secondsToTimeAllocation(450);  // returns "0:7.5"
 * secondsToTimeAllocation(3600); // returns "0:60"
 * secondsToTimeAllocation(0);    // returns "0:0"
 */
export const secondsToTimeAllocation = (seconds: number): string => {
	return `0:${seconds / 60}`;
};
